import {
  BarChart3,
  Calendar,
  CalendarDays,
  CheckCircle,
  Clock,
  FileSpreadsheet,
  Flag,
  LayoutGrid,
  Settings,
  Sparkles,
  Upload,
  UserCheck,
  Zap,
} from "lucide-react";
import { attendanceCalendarTabUrl } from "./attendanceCalendarMasterConfig";

/** Grouped layout for professional card arrangement */
export const ACTION_GROUPS = [
  {
    id: "master",
    label: "Master workspace",
    subtitle: "All attendance tools in one place",
    actionIds: ["calendar-hub", "ot-hours", "dashboard"],
    layout: "hero",
  },
  {
    id: "marking",
    label: "Mark attendance",
    subtitle: "Quick updates and today entry",
    actionIds: ["mark"],
    layout: "pair",
  },
  {
    id: "import",
    label: "Import & upload",
    subtitle: "Excel, CSV and bulk spreadsheet rows",
    actionIds: ["import"],
    layout: "pair",
  },
  {
    id: "calendar",
    label: "Calendar & holidays",
    subtitle: "Auto-marks and organisation holidays",
    actionIds: ["apply", "holidays"],
    layout: "pair",
  },
  {
    id: "shifts",
    label: "Shifts & roster",
    subtitle: "Shift master allocations and roster",
    actionIds: ["shifts", "roster"],
    layout: "pair",
  },
];

export const ATTENDANCE_PRIMARY_ACTIONS = [
  {
    id: "calendar-hub",
    label: "Attendance Calendar",
    desc: "Monthly marking, import, auto-mark, and lock attendance",
    path: "/attendance/apply-calendar",
    color: "purple",
    icon: CalendarDays,
    roles: ["admin", "hr"],
    featured: true,
  },
  {
    id: "ot-hours",
    label: "Overtime",
    desc: "Allocate overtime by day — syncs to payroll OVERTIME",
    path: "/attendance/ot-hours",
    color: "indigo",
    icon: Clock,
    roles: ["admin", "hr"],
    requiresOtHoursAllocation: true,
  },
  {
    id: "mark",
    label: "Mark Today",
    desc: "Single day quick entry",
    path: "/attendance/mark",
    color: "emerald",
    icon: UserCheck,
    roles: ["admin", "hr"],
  },
  {
    id: "import",
    label: "Import File",
    desc: "Excel / CSV — quick or full audit import",
    path: attendanceCalendarTabUrl("import"),
    color: "indigo",
    icon: Upload,
    roles: ["admin", "hr"],
  },
  {
    id: "apply",
    label: "Apply Calendar",
    desc: "Present on working days + H/WO",
    path: attendanceCalendarTabUrl("apply"),
    color: "violet",
    icon: Sparkles,
    roles: ["admin", "hr"],
  },
  {
    id: "holidays",
    label: "Holiday Calendar",
    desc: "Gazetted & restricted days",
    path: "/holiday-calendar",
    color: "amber",
    icon: Flag,
    roles: ["admin", "hr"],
  },
  {
    id: "dashboard",
    label: "Analytics",
    desc: "Workforce insights",
    path: "/attendance/dashboard",
    color: "cyan",
    icon: BarChart3,
    roles: ["admin", "hr"],
  },
  {
    id: "shifts",
    label: "Shift Management",
    desc: "Assign employees to shift templates from Settings master",
    path: "/attendance/shifts",
    color: "amber",
    icon: Clock,
    roles: ["admin", "hr"],
  },
  {
    id: "roster",
    label: "Roster Planner",
    desc: "Setup guide and monthly shift roster (separate from holidays)",
    path: "/attendance/roster",
    color: "teal",
    icon: LayoutGrid,
    roles: ["admin", "hr"],
  },
];

export const SECTION_THEMES = {
  setup: {
    icon: Settings,
    gradient: "linear-gradient(135deg, #d97706, #f59e0b)",
    dot: "#f59e0b",
  },
  daily: {
    icon: Calendar,
    gradient: "linear-gradient(135deg, #0d9488, #14b8a6)",
    dot: "#14b8a6",
  },
  leave: {
    icon: CheckCircle,
    gradient: "linear-gradient(135deg, #059669, #10b981)",
    dot: "#10b981",
  },
  payroll: {
    icon: FileSpreadsheet,
    gradient: "linear-gradient(135deg, #2563eb, #3b82f6)",
    dot: "#3b82f6",
  },
  workforce: {
    icon: Zap,
    gradient: "linear-gradient(135deg, #4f46e5, #7c3aed)",
    dot: "#6366f1",
  },
  admin: {
    icon: LayoutGrid,
    gradient: "linear-gradient(135deg, #475569, #64748b)",
    dot: "#64748b",
  },
};

export const ITEM_DOT_COLORS = {
  "/attendance/overview": "#14b8a6",
  "/attendance/mark": "#10b981",
  "/attendance/records": "#3b82f6",
  "/reports/attendance": "#2563eb",
  "/leave": "#10b981",
  "/leave/approval": "#3b82f6",
  "/leave/admin": "#ef4444",
  "/attendance/leave": "#94a3b8",
  "/holiday-calendar": "#f59e0b",
  "/attendance/dashboard": "#06b6d4",
  "/attendance/punches": "#8b5cf6",
  "/attendance/workforce": "#6366f1",
  "/attendance/shifts": "#f59e0b",
  "/attendance/roster": "#14b8a6",
  "/attendance/exceptions": "#ef4444",
  "/attendance/approvals": "#3b82f6",
  "/attendance/setup": "#f59e0b",
  "/attendance/onboarding": "#d97706",
  "/attendance/policies": "#64748b",
  "/attendance/freeze": "#ef4444",
  "/attendance/apply-calendar?tab=lock": "#ef4444",
  "/attendance/devices": "#06b6d4",
  "/attendance/policy-trace": "#8b5cf6",
  "/attendance/ops": "#475569",
  "/attendance/labels": "#94a3b8",
};

export function filterActionsByRole(actions, role) {
  const r = String(role || "").toLowerCase();
  return actions.filter((a) => a.roles.some((x) => String(x).toLowerCase() === r));
}

export function buildActionGroups(role, options = {}) {
  const otOn = options.otHoursAllocationEnabled !== false;
  let allowed = filterActionsByRole(ATTENDANCE_PRIMARY_ACTIONS, role);
  if (!otOn) {
    allowed = allowed.filter((a) => !a.requiresOtHoursAllocation);
  }
  const byId = Object.fromEntries(allowed.map((a) => [a.id, a]));

  return ACTION_GROUPS.map((group) => ({
    ...group,
    actions: group.actionIds.map((id) => byId[id]).filter(Boolean),
  })).filter((g) => g.actions.length > 0);
}
